import { useCallback, useRef } from 'react';
import { Box } from '@mui/material';
import { observer } from 'mobx-react';
import {
  useDataProvider,
  useLocalization,
  useMasterData,
  useSnackbar,
} from '@ui-framework/index';
import {
  getDefaultValuesForForm,
  IconLibrary,
  PageSection,
  PermissionButton,
} from '@product-framework/index';
import { useNavigate } from 'react-router-dom';
import Form from '@product-framework/components/AdvancedForms/Form';
import schema from './create.json';
import validationSchema from './validations';
import ServiceProviderLayout from '../../../components/ServiceProviderLayout';
import { withServiceProvider } from '../../../../../providers/ServiceProviderProvider';
import { useServiceModule } from '../../../../../providers/ServiceModuleProvider';

const FORM_ID = 'CreateServiceProviderCompanyForm';

function CompanyInfoCreate() {
  const { formatMessage } = useLocalization();
  const navigate = useNavigate();
  const formRef = useRef();
  const snackbar = useSnackbar();
  const masterData = useMasterData();

  const provider =
    process.env.NODE_ENV === 'development' ? 'sxclServices' : 'default';
  const { post, getApiUrl, get } = useDataProvider(provider);
  const { moduleSettings } = useServiceModule();

  const stackablePageButtons = [
    {
      label: formatMessage({
        id: 'LIST_SERVICE_PROVIDERS',
        defaultMessage: 'List Service Providers',
      }),
      permissionName: 'CAN_LIST_SP',
      Icon: IconLibrary.ServiceProviders,
      variant: 'outlined',
      onClick: () => navigate('/serviceproviders'),
    },
  ];

  const createSP = async data => {
    const url = `${getApiUrl()}/sxcl/v1/service-providers`;

    let address = {};

    if (data?.billingAddress && data?.billingAddressSessionToken) {
      address = {
        address: {
          addressType: 'MAIN',
          placeId: data?.billingAddress?.id,
          placeSessionToken: data?.billingAddressSessionToken,
        },
      };
    }

    const requestData = {
      ...(data.name && { name: data.name }),
      ...(data.accountNumber && { accountNumber: data.accountNumber }),
      ...(data.status && { status: data.status }),
      ...(data.fein && { fein: data.fein }),
      ...(data.websiteUrl && { websiteUrl: data.websiteUrl }),
      ...(data.bmsSystem && { bmsSystem: data.bmsSystem }),
      ...(data.contactFirstName ||
      data.contactLastName ||
      data.contactEmail ||
      data.contactPhoneNumber
        ? {
            contact: {
              ...(data.contactFirstName && {
                firstName: data.contactFirstName,
              }),
              ...(data.contactLastName && { lastName: data.contactLastName }),
              ...(data.contactEmail && { email: data.contactEmail }),
              ...(data.contactPhoneNumber && {
                phoneNumber: data.contactPhoneNumber,
              }),
            },
          }
        : {}),
      ...address,
      ...(data.defaultServiceTravelDistance && {
        defaultServiceTravelDistance: Number(data.defaultServiceTravelDistance),
      }),
    };
    const response = await post(url, requestData);
    return response;
  };

  const fetchDefaultValues = useCallback(async () => {
    try {
      const defaultInitialValues = {
        ...getDefaultValuesForForm({
          schema,
        }),
        defaultServiceTravelDistance:
          masterData?.defaultServiceTravelDistance || null,
        status: 'ACTIVE',
      };
      return { data: defaultInitialValues, errors: null };
    } catch (err) {
      const defaultInitialValues = {
        ...getDefaultValuesForForm({
          schema,
        }),
        status: 'ACTIVE',
      };
      return {
        data: defaultInitialValues,
        errors: [{ message: err.message }],
      };
    }
  }, [masterData?.defaultServiceTravelDistance, getDefaultValuesForForm]);

  const fetchSchema = useCallback(async () => {
    try {
      if (moduleSettings?.dispatchPurchased) {
        const dynamicSchema = schema.map(field => {
          if (field.name === 'defaultServiceTravelDistance') {
            return {
              ...field,
              required: true,
            };
          }
          return field;
        });
        return { data: dynamicSchema };
      }
      return { data: schema };
    } catch (err) {
      return { data: schema, errors: [{ message: err.message }] };
    }
  }, [moduleSettings?.dispatchPurchased]);

  const fetchValidationSchema = useCallback(async () => {
    try {
      const validations = validationSchema(
        formatMessage,
        getApiUrl,
        get,
        moduleSettings?.dispatchPurchased,
      );
      return {
        data: validations,
      };
    } catch (err) {
      return { data: null, errors: [{ message: err.message }] };
    }
  }, [moduleSettings?.dispatchPurchased]);

  const onSubmit = async data => {
    const response = await createSP(data);
    if (response?.ok) {
      snackbar.showMessage(
        formatMessage({
          id: 'SERVICEPROVIDER_WAS_SAVED_SUCCESSFULLY',
          defaultMessage: 'ServiceProvider was saved successfully',
        }),
      );

      return {
        nextValues: data,
        errors: null,
        callback: () =>
          navigate(`/serviceproviders/edit/${response?.data?.id}`),
      };
    }
    return {
      nextValues: data,
      errors: response?.errors,
    };
  };

  return (
    <ServiceProviderLayout
      stepperDisableExceptFirst
      showWarnings={false}
      stackablePageButtons={stackablePageButtons}>
      <PageSection
        label={formatMessage({
          id: 'SERVICE_PROVIDER_COMPANY_INFORMATION',
          defaultMessage: 'Service Provider Company Information',
        })}>
        <Form
          formId={FORM_ID}
          ref={formRef}
          permittedByDefault
          onSubmit={onSubmit}
          fetchSchema={fetchSchema}
          fetchDefaultValues={fetchDefaultValues}
          fetchValidationSchema={fetchValidationSchema}>
          <Box display="flex" justifyContent="center" gap={2} mt={3} mb={1}>
            <PermissionButton
              variant="contained"
              permissionName="CAN_UPDATE_SP_BASIC_INFO"
              startIcon={<IconLibrary.Save />}
              color="primary"
              onClick={() => formRef.current?.submit({ formId: FORM_ID })}>
              {formatMessage({
                id: 'SAVE_SERVICE_PROVIDER',
                defaultMessage: 'Save Service Provider',
              })}
            </PermissionButton>
            <PermissionButton
              variant="contained"
              permissionName="CAN_UPDATE_SP_BASIC_INFO"
              color="primary"
              startIcon={<IconLibrary.Reset />}
              onClick={() => {
                formRef.current?.reset();
              }}>
              {formatMessage({
                id: 'RESET',
                defaultMessage: 'Reset',
              })}
            </PermissionButton>
          </Box>
        </Form>
      </PageSection>
    </ServiceProviderLayout>
  );
}

const CompanyInfoCreateObservable = observer(CompanyInfoCreate);
export default withServiceProvider(CompanyInfoCreateObservable, {
  init: false,
});
